import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { isEmpty, noop } from 'lodash';
import { Modal } from '@fiverr-private/overlay';
import {
    TemplatesDataContext,
    InitialInteractionActionModal,
    HandlePaymentRequest,
} from '@fiverr-private/legal_documents_package';
import { ComplianceState } from '../../types/complianceTypes';
import { createPaymentSession, getBackgroundCheckResultFile, getOrganizationComplianceData } from '../../api';
import CustomModal from '../ComplianceModal/CustomModal';
import { ComplianceType, ComplianceTypes, QuestionsSubmitData } from '../ComplianceModal/types';
import { addQueryParam, getPageSource, redirectToPaymentsPage } from '../../utils';
import usePaymentSuccessToast from '../../hooks/usePaymentSuccessToast';
import { BackgroundCheckRequestSuccessPopup } from '../CompliacePopup/CompliacePopup';
import { reportEventsApi } from '../../biEvents';
import { ComplianceApi, ComplianceContext, Context } from './context';
import { backgroundCheckStatusRank, getLegalDocumentsRank, workforceClassificationRank } from './utils';
import { HandlePaymentRequestResponse, SellerComplianceStateType, OpenLegalDocInteractionModalProps } from './types';

function usePopup() {
    const [popup, setPopup] = useState<(() => ReactNode) | null>(null);

    const popupApi = useMemo(
        () => ({
            open(callback: () => ReactNode) {
                setPopup(() => callback);
            },
            close() {
                setPopup(() => null);
            },
        }),
        []
    );
    return [popup, popupApi] as const;
}

export const ComplianceProvider = ({
    children,
    organizationId,
    requester_id,
    complianceData: serverComplianceData,
    pageName = '',
}: {
    children: ReactNode;
    organizationId: string | undefined;
    requester_id: number | undefined;
    complianceData?: ComplianceState;
    pageName: string;
}) => {
    const [complianceData, setComplianceData] = useState<ComplianceState | null>(serverComplianceData ?? null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalProps, setModalProps] = useState({
        complianceType: ComplianceTypes.BACKGROUND_CHECK,
        sellerId: 0,
        sellerUserName: '',
    });
    const [initialInteractionActionModalOpen, setInitialInteractionActionModalOpen] = useState(false);
    const [initialInteractionActionModalProps, setInitialInteractionActionModalProps] =
        useState<OpenLegalDocInteractionModalProps>({
            legalDocsConfiguration: undefined,
            activeTemplates: 0,
            totalTemplates: 0,
            sellerName: '',
            sellerId: 0,
            handlePayment: noop as HandlePaymentRequest,
        });

    const hasServerComplianceData = !!serverComplianceData;

    const [popup, popupApi] = usePopup();

    const api: ComplianceApi = useMemo(
        () => ({
            async getCompliance() {
                if (!organizationId) {
                    return;
                }
                const body = await getOrganizationComplianceData(organizationId);
                return body;
            },
            async getBackgroundCheckResultFile({ externalVendorId }: { externalVendorId: string }) {
                if (!requester_id || !organizationId) {
                    return;
                }
                const body = await getBackgroundCheckResultFile({
                    requester_id,
                    externalVendorId,
                });
                return body;
            },
            async handlePayment({
                complianceType,
                currency,
                requesteeId,
                classificationAnswers,
                discountInPercents = 0,
                requeseteeUserName,
                templateIds,
                isFree,
            }: {
                complianceType: ComplianceType;
                requesteeId: number;
                currency?: string;
                classificationAnswers?: QuestionsSubmitData;
                discountInPercents?: number;
                requeseteeUserName: string;
                templateIds?: string[];
                isFree?: boolean;
            }): Promise<HandlePaymentRequestResponse> {
                if (!requester_id || !organizationId) {
                    return { isRedirecting: false };
                }
                const { paymentSessionId, subscriptionEventId } = await createPaymentSession({
                    requesteeId,
                    complianceType,
                    currency,
                    redirectUrl: addQueryParam(window.location.href, 'complianceSentType', complianceType),
                    source: getPageSource(),
                    discountInPercents: isFree ? 100 : discountInPercents,
                    auditAnswers: classificationAnswers,
                    templateIds,
                });

                if (paymentSessionId) {
                    redirectToPaymentsPage(paymentSessionId);
                    if (complianceType === ComplianceTypes.LEGAL_DOCS) {
                        reportEventsApi.clickContinueToPayment({
                            pageName,
                        });
                    }
                    return { isRedirecting: true };
                }

                const shouldRefetchData = subscriptionEventId && complianceType !== ComplianceTypes.LEGAL_DOCS;

                if (subscriptionEventId && complianceType !== ComplianceTypes.LEGAL_DOCS) {
                    popupApi.open(() => <BackgroundCheckRequestSuccessPopup name={requeseteeUserName} />);
                    setIsModalOpen(false);
                    getOrganizationComplianceData(organizationId)
                        .then((response) => {
                            setComplianceData(response);
                        })
                        .catch((err) => err);
                }

                return { isRedirecting: false, shouldRefetchData };
            },
        }),

        [organizationId, requester_id, popupApi, pageName]
    );

    const value: Context = useMemo(
        () => ({
            api,
            complianceData,
            pageName,
            openModal: (sellerId: number, complianceType: ComplianceTypes, sellerUserName: string) => {
                if (complianceType === ComplianceTypes.LEGAL_DOCS) {
                    // legal docs component handles modals opening internally, no external modals required
                    return;
                }
                setIsModalOpen(true);
                setModalProps({
                    sellerId,
                    complianceType,
                    sellerUserName,
                });
            },
            openLegalDocsInteractionModal: ({
                legalDocsConfiguration,
                activeTemplates,
                sellerName,
                totalTemplates,
                sellerId,
                handlePayment,
            }: OpenLegalDocInteractionModalProps) => {
                setInitialInteractionActionModalOpen(true);
                setInitialInteractionActionModalProps({
                    legalDocsConfiguration,
                    activeTemplates,
                    totalTemplates,
                    sellerName,
                    sellerId,
                    handlePayment,
                });
            },
            getSellerComplianceState(sellerId: number): SellerComplianceStateType {
                const backgroundChecks = complianceData?.backgroundCheckData?.[sellerId] ?? [];
                const backgroundCheckData = backgroundChecks[backgroundChecks.length - 1];
                const isSellerHasBackgroundCheckProcess = !isEmpty(backgroundCheckData) ?? false;

                const classificationRequests = complianceData?.workforceClassificationData?.[sellerId] ?? [];
                const classificationRequestData = classificationRequests[classificationRequests.length - 1];
                const isSellerHasClassificationProcess = !isEmpty(classificationRequestData) ?? false;

                const legalDocsData = complianceData?.legalDocsData;
                const isSellerHasSentLegalDocs = !isEmpty(legalDocsData?.statsBySeller?.[sellerId]?.statuses);

                const hasBackgroundCheckProcess = !isEmpty(complianceData?.backgroundCheckData);
                const hasClassificationRequestProcess = !isEmpty(complianceData?.workforceClassificationData);
                const hasLegalDocumentsProcess = !!legalDocsData?.totalTemplates;
                const hasComplianceProcess =
                    hasBackgroundCheckProcess || hasClassificationRequestProcess || hasLegalDocumentsProcess;
                const isSellerHasComplianceProcess =
                    isSellerHasBackgroundCheckProcess || isSellerHasClassificationProcess || isSellerHasSentLegalDocs;

                const backgroundCheckRank = backgroundCheckStatusRank(backgroundCheckData);

                const classificationRank = workforceClassificationRank(classificationRequestData);
                const legalDocsRank = getLegalDocumentsRank(legalDocsData?.statsBySeller?.[sellerId]);
                const compliaceRank = Math.max(backgroundCheckRank, classificationRank, legalDocsRank);

                const hasLegalDocsSetup = !!legalDocsData?.totalTemplates;

                const isBackgroundCheckRequestorYou = requester_id === backgroundCheckData?.requesterId;
                const isClassificationRequestorYou = requester_id === classificationRequestData?.requesterId;

                return {
                    backgroundCheckData,
                    hasBackgroundCheckProcess,
                    hasComplianceProcess,
                    compliaceRank,
                    backgroundCheckRank,
                    hasLegalDocsSetup,
                    isSellerHasBackgroundCheckProcess,
                    isSellerHasClassificationProcess,
                    classificationRequestData,
                    classificationRank,
                    isBackgroundCheckRequestorYou,
                    isClassificationRequestorYou,
                    isSellerHasComplianceProcess,
                    legalDocsInitialData: complianceData?.legalDocsData?.statsBySeller?.[sellerId],
                    legalDocsData,
                };
            },
            popupApi,
        }),
        [api, complianceData, popupApi, requester_id, pageName]
    );

    useEffect(() => {
        if (hasServerComplianceData) return;

        api.getCompliance()
            .then((response) => {
                setComplianceData(response);
            })
            .catch((err) => err);
    }, [api, organizationId, requester_id, hasServerComplianceData]);

    usePaymentSuccessToast();

    if (!organizationId || !requester_id) {
        return <>{children}</>;
    }

    return (
        <ComplianceContext.Provider value={value}>
            <TemplatesDataContext withTemplatesUsage={false}>
                {children}
                {isModalOpen && (
                    <CustomModal
                        isOpen={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                        complianceType={modalProps.complianceType}
                        requeseteeId={modalProps?.sellerId}
                        requeseteeUserName={modalProps.sellerUserName}
                    />
                )}
                <Modal centered onClose={popupApi.close} isOpen={!!popup}>
                    {popup?.()}
                </Modal>
                {initialInteractionActionModalOpen && (
                    <InitialInteractionActionModal
                        configurations={initialInteractionActionModalProps.legalDocsConfiguration}
                        activeTemplates={initialInteractionActionModalProps.activeTemplates}
                        totalTemplates={initialInteractionActionModalProps.totalTemplates}
                        sellerName={initialInteractionActionModalProps.sellerName}
                        sellerId={initialInteractionActionModalProps.sellerId}
                        handlePayment={initialInteractionActionModalProps.handlePayment}
                        isOpen={initialInteractionActionModalOpen}
                        biData={{ pageName }}
                        closeModal={() => setInitialInteractionActionModalOpen(false)}
                    />
                )}
            </TemplatesDataContext>
        </ComplianceContext.Provider>
    );
};
